<template>
  <div class="noti-wrapper">

    <!-- 🔄 Global Loader -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

   

    <!-- ============================
         📋 Notification List
    ============================= -->
    <div class="noti-list">

      <!-- Empty -->
      <div v-if="filteredNotifications.length === 0" class="empty-state">
        <i class="bx bx-bell-off"></i>
        No notifications found
      </div>

      <!-- List -->
      <div
        v-for="noti in paginated"
        :key="noti.notification_id"
        class="noti-card fade-in"
      >
        <div>
          <h4>{{ noti.title }}</h4>
          <p>{{ noti.desc }}</p>
          <span class="date">{{ noti.datetime }}</span>
        </div>

        <button class="delete-btn" @click="confirmDelete(noti)">
          <i class="bx bx-trash"></i>
        </button>
      </div>

    </div>

    <!-- ============================
         📄 Pagination
    ============================= -->
    <div class="pagination-row" v-if="filteredNotifications.length > 0">
      <button @click="prevPage" :disabled="page === 1">
        <i class="bx bx-chevron-left"></i>
      </button>

      <span class="pager">{{ page }} / {{ totalPages }}</span>

      <button @click="nextPage" :disabled="page === totalPages">
        <i class="bx bx-chevron-right"></i>
      </button>
    </div>

    <!-- ============================
         ❗ Confirm Dialog
    ============================= -->
    <ConfirmDialog
      :show="showConfirm"
      title="Delete Notification"
      :message="confirmMessage"
      @cancel="showConfirm = false"
      @confirmAction="performDelete"
    />

  </div>
</template>

<script>
import {
  getNotifications,
  deleteNotification
} from "@/apis/authapis";

import ConfirmDialog from "@/comps/ConfirmDialog.vue";
import { useToast } from "vue-toastification";

export default {
  name: "NotificationList",
  components: { ConfirmDialog },

  data() {
    return {
      loading: false,
      toast: useToast(),

      notifications: [],
      search: "",
      filterType: "",
      page: 1,
      perPage: 6,

      showConfirm: false,
      deleteTarget: null,
    };
  },

  computed: {
    filteredNotifications() {
      let data = this.notifications;

      if (this.filterType !== "") {
        data = data.filter(n => n.noti_type === this.filterType);
      }

      if (this.search.trim() !== "") {
        const q = this.search.toLowerCase();
        data = data.filter(n =>
          `${n.title} ${n.desc}`.toLowerCase().includes(q)
        );
      }

      return data;
    },

    totalPages() {
      return Math.ceil(this.filteredNotifications.length / this.perPage) || 1;
    },

    paginated() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredNotifications.slice(start, start + this.perPage);
    },

    confirmMessage() {
      return this.deleteTarget
        ? `Delete "${this.deleteTarget.title}" permanently?`
        : "";
    }
  },

  methods: {
    resetPagination() {
      this.page = 1;
    },

    async loadNotifications() {
      this.loading = true;

      const ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");

      const res = await getNotifications(ldata.user_id, this.page - 1);

      this.loading = false;

      if (res.status && res.records) {
        this.notifications = res.records;
      } else {
        this.notifications = [];
      }
    },

    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
      }
    },

    prevPage() {
      if (this.page > 1) {
        this.page--;
      }
    },

    confirmDelete(noti) {
      this.deleteTarget = noti;
      this.showConfirm = true;
    },

    async performDelete() {
      this.showConfirm = false;

      const res = await deleteNotification(this.deleteTarget.notification_id);

      if (res.status) {
        this.toast.success("Notification deleted");
        this.notifications = this.notifications.filter(
          n => n.notification_id !== this.deleteTarget.notification_id
        );
      } else {
        this.toast.error(res.success || "Delete failed");
      }

      this.deleteTarget = null;
    }
  },

  mounted() {
    this.loadNotifications();
  }
};
</script>

<style scoped>
.noti-wrapper {
  padding: 20px;
}

/* ===============================
   🔍 Search + Filters
================================= */
.filter-bar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.search-box {
  display: flex;
  align-items: center;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 10px;
  flex: 1;
  min-width: 240px;
}

.search-box i {
  color: var(--muted);
  font-size: 1rem;
}

.search-box input {
  border: none;
  outline: none;
  margin-left: 8px;
  width: 100%;
  background: transparent;
  font-size: 13px;
  color: var(--text);
}

.filter-bar select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--text);
  font-size: 13px;
  cursor: pointer;
  min-width: 150px;
  transition: 0.25s ease;
}

.filter-bar select:hover {
  border-color: var(--primary);
}

/* ===============================
   📋 Notification Cards
================================= */
.noti-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.noti-card {
  background: var(--card);
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  transition: 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.noti-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.08);
}

.noti-card h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.noti-card p {
  margin: 4px 0;
  font-size: 13px;
  color: var(--muted);
  line-height: 1.4;
}

.date {
  display: inline-block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
}

/* ===============================
   🗑 Delete Button
================================= */
.delete-btn {
  border: none;
  background: #fee2e2;
  color: #dc2626;
  font-size: 1.2rem;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.25s ease;
}

.delete-btn:hover {
  background: #fecaca;
}

/* ===============================
   😴 Empty State
================================= */
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: var(--muted);
  font-size: 14px;
}

.empty-state i {
  font-size: 2rem;
  display: block;
  margin-bottom: 6px;
  opacity: 0.7;
}

/* ===============================
   📄 Pagination
================================= */
.pagination-row {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
}

.pagination-row button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.25s ease;
  font-size: 14px;
}

.pagination-row button:hover:not(:disabled) {
  background: #e5e7eb;
  transform: translateY(-2px);
}

.pagination-row button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pager {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

/* ===============================
   🎞 Animations
================================= */
.fade-in {
  animation: fadeInUp 0.45s ease forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* ===============================
   📱 RESPONSIVE
================================= */
@media (max-width: 768px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-bar select {
    width: 100%;
  }

  .search-box {
    width: 100%;
  }

  .noti-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .delete-btn {
    align-self: flex-end;
  }
}
</style>
